import {
  AdvancedDomainFraudCheck,
  ApplicationFraudCheck,
  BasicDomainFraudCheck,
  DocumentFraudCheck,
  FraudCheck,
  FraudCheckResultsResponse
} from '@percent/admin-dashboard/api/types'
import { FailedDomainAutoChecks, getFailedDomainAutoChecks } from './getFailedDomainAutoChecks'

type FlattenDomainCheck<T> = T extends { checkName: infer N; analysedDomains: (infer AD)[] }
  ? { checkName: N; data: AD; weight: number; result: 'fail' | 'pass' }
  : never

export type BasicDomainCheck = FlattenDomainCheck<BasicDomainFraudCheck['data']>
export type AdvancedDomainCheck = FlattenDomainCheck<AdvancedDomainFraudCheck['data']>

type AgentCheck = FraudCheck & ApplicationFraudCheck
export type DocumentCheck = FraudCheck & DocumentFraudCheck

export type DomainFraudChecks = {
  advanced: AdvancedDomainCheck[]
  basic: BasicDomainCheck[]
}

type RawDomain = {
  name: string
  fraudChecks: DomainFraudChecks
}

type RawGrouped = {
  agent: AgentCheck[]
  domains: RawDomain[]
  documents: DocumentCheck[]
}

export type GroupedFraudChecks = {
  agent: {
    fraudChecks: AgentCheck[]
    failedChecks: ApplicationFraudCheck['data'][]
  }
  domains: {
    name: string
    fraudChecks: DomainFraudChecks
    failedChecks: FailedDomainAutoChecks
  }[]
  documents: {
    fraudChecks: DocumentCheck[]
    failedChecks: DocumentFraudCheck['data'][]
  }
}

const addDomainCheckToGroup = (
  domains: RawDomain[],
  domainName: string,
  check: AdvancedDomainCheck | BasicDomainCheck,
  type: 'advanced_domain_check' | 'basic_domain_check'
) => {
  const domainItem = domains.find(d => d.name === domainName)
  const fraudCheckType = (() => {
    switch (type) {
      case 'advanced_domain_check':
        return 'advanced'
      case 'basic_domain_check':
        return 'basic'
      default:
        return 'basic'
    }
  })()

  if (domainItem) {
    domainItem.fraudChecks[fraudCheckType].push(check as never)
  } else {
    domains.push({
      name: domainName,
      fraudChecks: {
        advanced: fraudCheckType === 'advanced' ? [check as AdvancedDomainCheck] : [],
        basic: fraudCheckType === 'basic' ? [check as BasicDomainCheck] : []
      }
    })
  }
}

const getFlags = (analysedDomain: object): unknown[] =>
  'flags' in analysedDomain && Array.isArray(analysedDomain.flags) ? analysedDomain.flags : []

export const getGroupedFraudChecks = (
  fraudCheckResults?: FraudCheckResultsResponse | null
): GroupedFraudChecks | null => {
  const raw = fraudCheckResults?.checkResults.reduce<RawGrouped>(
    (acc, curr) => {
      if (curr.type === 'application_check') {
        acc.agent.push(curr)
      } else if (
        (curr.type === 'advanced_domain_check' || curr.type === 'basic_domain_check') &&
        // A domain check without analysedDomains (e.g. has_email_domain_redirect_to_organisation) can't be grouped by domain
        'analysedDomains' in curr.data
      ) {
        const isFlaggedOnSomeDomain = curr.data.analysedDomains.some(ad => getFlags(ad).length > 0)

        curr.data.analysedDomains.forEach(ad => {
          // NOTE: it has to match as it's static shape returned from BE. Treat this const as type guard
          const check = {
            checkName: curr.data.checkName,
            data: ad,
            weight: curr.data.weight,
            // A failing check that flags some of its domains failed on those domains only
            result: isFlaggedOnSomeDomain && getFlags(ad).length === 0 ? 'pass' : curr.result
          } as AdvancedDomainCheck | BasicDomainCheck
          addDomainCheckToGroup(acc.domains, ad.domain, check, curr.type)
        })
      } else if (curr.type === 'document_check') {
        acc.documents.push(curr)
      }

      return acc
    },
    { agent: [], domains: [], documents: [] }
  )

  if (!raw) return null

  const sortByWeightDesc = <T extends { weight: number }>(a: T, b: T) => b.weight - a.weight

  return {
    agent: {
      fraudChecks: raw.agent,
      failedChecks: raw.agent
        .filter(f => f.result === 'fail')
        .map(f => f.data)
        .sort(sortByWeightDesc)
    },
    domains: raw.domains.map(domain => {
      const failedChecks = getFailedDomainAutoChecks(domain.fraudChecks)

      return {
        ...domain,
        failedChecks: {
          ...failedChecks,
          basic: failedChecks.basic?.sort(sortByWeightDesc),
          advanced: failedChecks.advanced?.sort(sortByWeightDesc)
        }
      }
    }),
    documents: {
      fraudChecks: raw.documents,
      failedChecks: raw.documents
        .filter(f => f.result === 'fail')
        .map(f => f.data)
        .sort(sortByWeightDesc)
    }
  }
}
